// plan-02 S04: lens dirt pass — 7 Gaussian smudges, 4s crossfade, 0.5Hz breathing

import type { DrawPass } from '../canvas'

const SMEAR_COUNT = 7
const CROSSFADE_S = 4
const BREATH_HZ = 0.5

interface Smear {
  x: number
  y: number
  r: number
}

function seededSmears(seed: number, w: number, h: number): Smear[] {
  const out: Smear[] = []
  // simple deterministic scatter from seed
  let s = seed
  function next(): number {
    s = (s * 1103515245 + 12345) & 0x7fffffff
    return s / 0x7fffffff
  }
  for (let i = 0; i < SMEAR_COUNT; i++) {
    out.push({
      x: next() * w,
      y: next() * h,
      r: 40 + next() * 80,
    })
  }
  return out
}

function drawSmears(ctx: CanvasRenderingContext2D, smears: Smear[], alpha: number): void {
  for (const s of smears) {
    const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r)
    grad.addColorStop(0, `rgba(10, 26, 10, ${alpha})`)
    grad.addColorStop(1, 'rgba(10, 26, 10, 0)')
    ctx.fillStyle = grad
    ctx.fillRect(s.x - s.r, s.y - s.r, s.r * 2, s.r * 2)
  }
}

export function createLensDirt(reduced: boolean): DrawPass {
  let prevSmears: Smear[] | null = null
  let curSmears: Smear[] | null = null
  let lastSwitch = -Infinity

  return (ctx, w, h, t): void => {
    const epoch = Math.floor(t / CROSSFADE_S)
    if (!curSmears || epoch !== lastSwitch) {
      prevSmears = curSmears
      curSmears = seededSmears(epoch + 1, w, h)
      lastSwitch = epoch
    }

    // crossfade progress
    const frac = (t - epoch * CROSSFADE_S) / CROSSFADE_S
    const breath = reduced ? 0 : Math.sin(t * BREATH_HZ * Math.PI * 2) * 0.12
    const baseAlpha = 0.03 + breath

    if (prevSmears && frac < 1) {
      drawSmears(ctx, prevSmears, baseAlpha * (1 - frac))
    }
    drawSmears(ctx, curSmears, baseAlpha * frac)
  }
}
